import { useEffect } from '@wordpress/element';
const { useSelect } = wp.data;
const { registerBlockType } = wp.blocks;
const { InspectorControls, RichText } = wp.blockEditor;
const {
  Button,
  TextControl,
  SelectControl,
  PanelBody,
  ToggleControl,
  CheckboxControl,
} = wp.components;
const { Fragment, useState } = wp.element;
import 'moment-timezone';
import { initCopy } from '../../../resources/scripts/module/utils';
import modal from '../../../resources/scripts/module/modal';
const { __ } = wp.i18n;

registerBlockType('pg/people', {
  title: 'PG People Block',
  icon: 'groups',
  category: 'layout',
  attributes: {
    typesJson: { type: 'string', default: '[]' },
    types: {
      type: 'array',
      default: [],
      source: 'query',
      selector: '.people-type',
      query: {
        typeName: {
          type: 'string',
          source: 'text',
          selector: '.type-name',
        },
        people: {
          type: 'array',
          default: [],
          source: 'query',
          selector: '.person-card',
          query: {
            id: { type: 'number', source: 'attribute', attribute: 'data-id' },
          },
        },
        itemSize: { type: 'string', default: 'big' },
        showTypeName: { type: 'boolean', default: true },
        showDescription: { type: 'boolean', default: false },
        columns: { type: 'string', default: '3' },
      },
    },
    enableSectionClass: { type: 'boolean', default: false },
    sectionClass: { type: 'string', default: 'section-light-primary' },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-dark-secondary',
    },
    sectionTitle: { type: 'string', default: '' },
    sectionDescription: { type: 'string', default: '' },
    layout: { type: 'string', default: 'default' },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      typesJson,
      enableSectionClass,
      sectionClass,
      cornerPositionClass,
      cornerBackgroundClass,
      cornerClass,
      sectionTitle,
      sectionDescription,
      layout,
    } = attributes;
    const [types, setTypes] = useState(
      attributes.typesJson ? JSON.parse(attributes.typesJson) : []
    );
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
      setLoaded(true);
    }, [loaded]);

    useEffect(() => {
      modal();
    }, [types.length, modal, loaded]);

    const allPeople = useSelect((select) => {
      return select('core').getEntityRecords('postType', 'people', {
        per_page: -1,
      });
    }, []);

    const peopleOptions = allPeople
      ? allPeople.map((person) => ({
          label: person.acf?.person_name || person.title.rendered,
          value: person.id,
        }))
      : [];

    const handleAddType = () => {
      const newType = {
        typeName: '',
        showTypeName: false,
        showDescription: false,
        columns: 3,
        people: [],
      };
      setTypes([...types, newType]);
      setAttributes({
        types: [...types, newType],
        typesJson: JSON.stringify([...types, newType]),
      });
    };

    const updateTypeName = (index, name) => {
      const updatedTypes = [...types];
      updatedTypes[index].typeName = name;
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const updateTypeField = (index, field, value) => {
      const updatedTypes = [...types];
      updatedTypes[index][field] = value;
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const addPersonToType = (typeIndex, selectedIds) => {
      const newPeople = selectedIds
        .filter(
          (id) => !types[typeIndex].people.some((person) => person.id === id)
        )
        .map((id) => ({
          id,
          showButton: false,
          buttonLabel: 'Read biography',
        }));
      if (newPeople.length > 0) {
        const updatedTypes = [...types];
        updatedTypes[typeIndex].people = [
          ...updatedTypes[typeIndex].people,
          ...newPeople,
        ];
        setTypes(updatedTypes);
        setAttributes({
          types: updatedTypes,
        });
        setAttributes({ types: types, typesJson: JSON.stringify(types) });
      }
    };

    const removePersonFromType = (typeIndex, personIndex) => {
      const updatedTypes = [...types];
      updatedTypes[typeIndex].people.splice(personIndex, 1);
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
      });
      setAttributes({ types: types, typesJson: JSON.stringify(types) });
    };

    const toggleShowButton = (typeIndex, personIndex) => {
      const updatedTypes = [...types];
      const person = updatedTypes[typeIndex].people[personIndex];
      person.showButton = !person.showButton;
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const updateButtonLabel = (typeIndex, personIndex, label) => {
      const updatedTypes = [...types];
      updatedTypes[typeIndex].people[personIndex].buttonLabel = label;
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const removeType = (typeIndex) => {
      const updatedTypes = [...types];
      updatedTypes.splice(typeIndex, 1);
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const SelectPeopleControl = ({
      typeIndex,
      peopleOptions,
      selectedPeopleIds,
      onSelectedChange,
    }) => {
      return (
        <SelectControl
          multiple
          label="Select People"
          value={selectedPeopleIds}
          options={peopleOptions}
          onChange={(selectedIds) => {
            onSelectedChange(
              typeIndex,
              selectedIds.map((id) => parseInt(id, 10))
            );
          }}
        />
      );
    };

    const Wrapper = enableSectionClass ? 'section' : 'div';
    const SectionWrapper = enableSectionClass ? 'div' : Fragment;

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title="Section Settings">
            <CheckboxControl
              label="Enable Section Class"
              checked={enableSectionClass}
              onChange={(value) => setAttributes({ enableSectionClass: value })}
            />
            {enableSectionClass && (
              <>
                <SelectControl
                  label={__('Section Class')}
                  value={sectionClass}
                  options={[
                    {
                      label: 'Dark Secondary',
                      value: 'section-dark-secondary',
                    },
                    { label: 'Dark Primary', value: 'section-dark-primary' },
                    { label: 'Light Primary', value: 'section-light-primary' },
                    {
                      label: 'Light Secondary',
                      value: 'section-light-secondary',
                    },
                  ]}
                  onChange={(newClass) =>
                    setAttributes({ sectionClass: newClass })
                  }
                />
                <SelectControl
                  label={__('Corner Position')}
                  value={cornerPositionClass}
                  options={[
                    { label: 'None', value: 'corner-disabled' },
                    { label: 'Left-Top', value: 'corner-left-top' },
                    { label: 'Left-Bottom', value: 'corner-left-bottom' },
                    { label: 'Right-Top', value: 'corner-right-top' },
                    { label: 'Right-Bottom', value: 'corner-right-bottom' },
                  ]}
                  onChange={(value) =>
                    setAttributes({ cornerPositionClass: value })
                  }
                />
                <SelectControl
                  label={__('Corner Background')}
                  value={cornerBackgroundClass}
                  options={[
                    { label: 'Dark Primary', value: 'corner-on-dark-primary' },
                    {
                      label: 'Dark Secondary',
                      value: 'corner-on-dark-secondary',
                    },
                    {
                      label: 'Light Primary',
                      value: 'corner-on-light-primary',
                    },
                    {
                      label: 'Light Secondary',
                      value: 'corner-on-light-secondary',
                    },
                  ]}
                  onChange={(value) =>
                    setAttributes({ cornerBackgroundClass: value })
                  }
                />
                <SelectControl
                  label={__('Layout')}
                  value={layout}
                  options={[
                    { label: __('Default'), value: 'default' },
                    { label: __('Centered'), value: 'centered' },
                  ]}
                  onChange={(value) => setAttributes({ layout: value })}
                />
              </>
            )}
          </PanelBody>
          <Button isSecondary onClick={handleAddType}>
            Add Type
          </Button>
          {types.map((type, typeIndex) => (
            <PanelBody
              title={`Type ${typeIndex + 1}`}
              initialOpen={true}
              key={typeIndex}
            >
              <SelectControl
                label="Columns"
                value={type.columns}
                options={[
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                ]}
                onChange={(value) =>
                  updateTypeField(typeIndex, 'columns', value)
                }
              />
              <SelectControl
                label="Cards Size"
                value={type.itemSize}
                options={[
                  { label: 'Small', value: 'small' },
                  { label: 'Medium', value: 'medium' },
                  { label: 'Big', value: 'big' },
                  { label: 'Large', value: 'large' },
                  { label: 'Rectangle', value: 'rectangle' },
                ]}
                onChange={(value) =>
                  updateTypeField(typeIndex, 'itemSize', value)
                }
              />
              <CheckboxControl
                label="Show Type Name"
                checked={type.showTypeName}
                onChange={(value) =>
                  updateTypeField(typeIndex, 'showTypeName', value)
                }
              />
              <CheckboxControl
                label="Show description"
                checked={type.showDescription}
                onChange={(value) =>
                  updateTypeField(typeIndex, 'showDescription', value)
                }
              />
              <TextControl
                label="Type Name"
                value={type.typeName}
                onChange={(name) => updateTypeName(typeIndex, name)}
              />
              {type.people.map((person, personIndex) => (
                <div key={person.id}>
                  <span>
                    {(allPeople &&
                      allPeople.find((p) => p.id === person.id)?.title
                        .rendered) ||
                      'Unknown'}
                  </span>
                  <Button
                    isDestructive
                    onClick={() => removePersonFromType(typeIndex, personIndex)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <SelectPeopleControl
                typeIndex={typeIndex}
                peopleOptions={peopleOptions}
                selectedPeopleIds={type.people.map((p) => p.id)}
                onSelectedChange={addPersonToType}
              />
              <Button isDestructive onClick={() => removeType(typeIndex)}>
                Remove Type
              </Button>
            </PanelBody>
          ))}
        </InspectorControls>

        <Wrapper
          className={`people-list-block people-list-block-${layout} ${
            enableSectionClass ? sectionClass : ''
          }`}
        >
          {enableSectionClass && (
            <div
              className={`corner ${cornerPositionClass} ${sectionClass.replace(
                'section',
                'corner'
              )} ${cornerBackgroundClass}`}
            ></div>
          )}
          <SectionWrapper
            className={enableSectionClass ? 'section-wrapper' : ''}
          >
            {enableSectionClass && (
              <>
                <RichText
                  tagName="h2"
                  value={sectionTitle}
                  onChange={(newTitle) =>
                    setAttributes({ sectionTitle: newTitle })
                  }
                  placeholder={__('Title')}
                />
                <div className={'description'}>
                  <RichText
                    tagName={'p'}
                    value={sectionDescription}
                    onChange={(newDescription) =>
                      setAttributes({ sectionDescription: newDescription })
                    }
                    placeholder={__('Description')}
                  />
                </div>
              </>
            )}

            {loaded &&
              types.map((type, typeIndex) => (
                <>
                  {type.showTypeName && (
                    <h3 className="type-name">
                      {type.typeName || `Type ${typeIndex + 1}`}
                    </h3>
                  )}
                  <div
                    className={`cards cards-size-${type.itemSize} cards-columns-${type.columns}`}
                  >
                    {type.people.map((person, personIndex) => {
                      const personDetails = allPeople
                        ? allPeople.find(
                            (p) => parseInt(p.id) === parseInt(person.id)
                          )
                        : [];
                      const personUrl = personDetails
                        ? `/wp-admin/admin-ajax.php?action=person_details&personId=${personDetails.id}`
                        : '#';

                      return (
                        <div
                          className="person-card"
                          key={person.id}
                          data-id={person.id}
                        >
                          <div className="image">
                            <img
                              src={
                                personDetails
                                  ? personDetails?.featured_image_url
                                  : 'placeholder.jpg'
                              }
                              alt={
                                personDetails
                                  ? personDetails?.acf?.person_name
                                  : 'Placeholder'
                              }
                            />
                          </div>
                          <div className="info">
                            <p className="name">
                              {personDetails?.acf?.person_name}
                            </p>
                            <p className="position">
                              {personDetails?.acf?.person_title}
                            </p>
                            <p className="company">
                              {personDetails?.acf?.person_company}
                            </p>
                            {type.showDescription && (
                              <p
                                className="about"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    personDetails?.excerpt?.rendered || '',
                                }}
                              ></p>
                            )}
                          </div>
                          <ToggleControl
                            label="Show Button?"
                            className={'showButtonToggle'}
                            checked={person.showButton}
                            onChange={() =>
                              toggleShowButton(typeIndex, personIndex)
                            }
                          />
                          {person.showButton && (
                            <Button
                              data-modal-trigger={true}
                              className={'btn btn-dark btn-text btn-biography'}
                              onClick={() => {}}
                              data-ajax-url={personUrl}
                            >
                              <RichText
                                tagName="span"
                                value={person.buttonLabel}
                                placeholder={'Read More'}
                                onChange={(value) =>
                                  updateButtonLabel(
                                    typeIndex,
                                    personIndex,
                                    value
                                  )
                                }
                                keepPlaceholderOnFocus={true}
                              />
                            </Button>
                          )}
                          <Button
                            className={'personRemoveButton'}
                            isDestructive
                            onClick={() =>
                              removePersonFromType(typeIndex, personIndex)
                            }
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </>
              ))}
          </SectionWrapper>
        </Wrapper>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    if (attributes.enableSectionClass) {
      return (
        <section
          className={`people-list-block people-list-block-${attributes.layout} ${
            attributes.enableSectionClass ? attributes.sectionClass : ''
          }`}
          data-people={attributes.typesJson}
        >
          {attributes.enableSectionClass && (
            <div
              className={`corner ${attributes.cornerPositionClass} ${attributes.sectionClass.replace(
                'section',
                'corner'
              )} ${attributes.cornerBackgroundClass}`}
            ></div>
          )}
        </section>
      );
    }

    return (
      <div
        className={`people-list-block people-list-block-${attributes.layout}`}
        data-people={attributes.typesJson}
      ></div>
    );
  },
});
